import React from 'react';
import { QualityCheck as QC } from '../types';

interface QualityCheckProps {
  quality: QC;
}

export const QualityCheck: React.FC<QualityCheckProps> = ({ quality }) => {
  const checks = [
    {
      label: 'Valid R² (≥0.95)',
      passed: quality.validR2,
      description: 'Linear model fits data well'
    },
    {
      label: 'Uniform Load Spacing',
      passed: quality.uniformSpacing,
      description: 'Even distribution of test loads'
    },
    {
      label: 'Free Sprint Present',
      passed: quality.freeSprintPresent,
      description: 'Unresisted sprint included'
    },
    {
      label: 'Sufficient Data Points (≥4)',
      passed: quality.sufficientDataPoints,
      description: 'Enough trials for reliable analysis'
    },
    {
      label: 'Adequate Velocity Range (≥50%)',
      passed: quality.adequateVelocityRange,
      description: 'Wide enough velocity span'
    }
  ];

  const passedCount = checks.filter(c => c.passed).length;
  const totalCount = checks.length;

  return (
    <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-2xl font-semibold tracking-tight text-theme-text">Data Quality</h3>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-lg text-sm font-semibold border ${
            passedCount === totalCount 
              ? 'bg-green-950 text-green-400 border-green-800' 
              : passedCount >= totalCount * 0.8
              ? 'bg-amber-950 text-amber-400 border-amber-800'
              : 'bg-red-950 text-red-400 border-red-800'
          }`}>
            {passedCount}/{totalCount} Passed
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {checks.map((check, index) => (
          <div key={index} className="flex items-center space-x-3">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
              check.passed ? 'bg-green-600' : 'bg-red-600'
            }`}>
              {check.passed ? (
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div>
              <p className={`font-medium ${check.passed ? 'text-theme-text' : 'text-red-400'}`}>
                {check.label}
              </p>
              <p className="text-base text-theme-muted">{check.description}</p>
            </div>
          </div>
        ))}
      </div>

      {passedCount < totalCount && (
        <div className="mt-4 p-3 bg-amber-950/20 border border-amber-800 rounded-lg">
          <p className="text-base text-amber-400">
            <span className="font-semibold">Warning:</span> Some quality checks failed. Consider retesting with improved protocol.
          </p>
        </div>
      )}
    </div>
  );
};